import React, { useState, useEffect, useCallback } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import Upload from 'lucide-react/dist/esm/icons/upload';
import History from 'lucide-react/dist/esm/icons/history';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';

type WalkthroughMode = 'new_user' | 'analysis_view';
type StepPosition = 'center' | 'top' | 'bottom' | 'left' | 'right';

interface WalkthroughStep {
  id: string;
  target: string | null;
  title: string;
  description: string;
  icon: React.ReactNode;
  position: StepPosition;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const NEW_USER_STEPS: WalkthroughStep[] = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to QUAZI! 🚀',
    description: 'Your AI-powered financial analysis platform. Let\'s get you started with your first analysis!',
    icon: <Sparkles size={32} />,
    position: 'center'
  },
  {
    id: 'upload',
    target: '[data-walkthrough="upload"]',
    title: 'Upload Your First Document',
    description: 'Drag & drop a PDF file here — P&L statements, balance sheets, or annual reports. QUAZI will analyze it automatically.',
    icon: <Upload size={24} />,
    position: 'bottom'
  },
  {
    id: 'complete',
    target: null,
    title: 'Ready to Go! 🎉',
    description: 'Upload a document and QUAZI will extract key insights, generate charts, and let you chat about the data.',
    icon: <Sparkles size={32} />,
    position: 'center'
  }
];

const ANALYSIS_VIEW_STEPS: WalkthroughStep[] = [
  {
    id: 'welcome-back',
    target: null,
    title: 'Great! Your Analysis is Ready 🎉',
    description: 'Let me show you how to get the most out of your analysis results.',
    icon: <Sparkles size={32} />,
    position: 'center'
  },
  {
    id: 'history',
    target: '[data-walkthrough="sidebar"]',
    title: 'Your Analysis History',
    description: 'All your analyses are saved here. Click any to view it, or use the search to find specific ones.',
    icon: <History size={24} />,
    position: 'right'
  },
  {
    id: 'export',
    target: '[data-walkthrough="export"]',
    title: 'Export PDF Reports',
    description: 'Generate professional reports with one click. Perfect for sharing with stakeholders or keeping records.',
    icon: <FileText size={24} />,
    position: 'bottom'
  },
  {
    id: 'complete',
    target: null,
    title: "You're All Set! ✨",
    description: 'You now know all the key features. Happy analyzing!',
    icon: <Sparkles size={32} />,
    position: 'center'
  }
];

interface WalkthroughProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  mode?: WalkthroughMode;
}

const Walkthrough: React.FC<WalkthroughProps> = ({ isOpen, onClose, onComplete, mode = 'new_user' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const STEPS = mode === 'analysis_view' ? ANALYSIS_VIEW_STEPS : NEW_USER_STEPS;
  const step = STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;

  useEffect(() => {
    setCurrentStep(0);
  }, [mode]);

  const updateTargetPosition = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(step.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16
      });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (isOpen) {
      updateTargetPosition();
      window.addEventListener('resize', updateTargetPosition);
      window.addEventListener('scroll', updateTargetPosition);
    }
    return () => {
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [isOpen, currentStep, updateTargetPosition]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleSkip = () => {
    if (mode === 'new_user') {
      localStorage.setItem('quazi_walkthrough_new_user', 'true');
    } else {
      localStorage.setItem('quazi_walkthrough_analysis', 'true');
    }
    onClose();
  };

  const handleComplete = () => {
    if (mode === 'new_user') {
      localStorage.setItem('quazi_walkthrough_new_user', 'true');
    } else {
      localStorage.setItem('quazi_walkthrough_analysis', 'true');
    }
    onComplete?.();
    onClose();
  };

  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect || step.position === 'center') {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const padding = 20;
    const tooltipWidth = 380;
    const tooltipHeight = 200;

    switch (step.position) {
      case 'bottom':
        return {
          position: 'fixed',
          top: `${targetRect.top + targetRect.height + padding}px`,
          left: `${Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding))}px`
        };
      case 'top':
        return {
          position: 'fixed',
          top: `${targetRect.top - tooltipHeight - padding}px`,
          left: `${Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding))}px`
        };
      case 'left':
        return {
          position: 'fixed',
          top: `${Math.max(padding, targetRect.top + targetRect.height / 2 - tooltipHeight / 2)}px`,
          left: `${targetRect.left - tooltipWidth - padding}px`
        };
      case 'right':
        return {
          position: 'fixed',
          top: `${Math.max(padding, targetRect.top + targetRect.height / 2 - tooltipHeight / 2)}px`,
          left: `${targetRect.left + targetRect.width + padding}px`
        };
      default:
        return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-toast">
      {/* Spotlight effect */}
      {targetRect && (
        <>
          <svg width="100%" height="100%" className="absolute inset-0">
            <defs>
              <mask id="spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect 
                  x={targetRect.left} 
                  y={targetRect.top} 
                  width={targetRect.width} 
                  height={targetRect.height} 
                  rx="12"
                  fill="black" 
                />
              </mask>
            </defs>
            <rect 
              x="0" y="0" width="100%" height="100%" 
              fill="rgba(0, 0, 0, 0.85)" 
              mask="url(#spotlight-mask)" 
            />
          </svg>
          {/* Highlight border */}
          <div 
            className="absolute border-2 border-brand rounded-xl shadow-[0_0_20px_rgba(33,158,188,0.5),0_0_40px_rgba(33,158,188,0.3)] animate-pulse pointer-events-none"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height
            }}
          />
        </>
      )}

      {/* Full overlay when no target */}
      {!targetRect && (
        <div className="absolute inset-0 bg-black/85" />
      )}

      {/* Tooltip */}
      <div 
        className={`w-[380px] p-8 bg-gradient-to-br from-[#0f172a]/98 to-[#1e1b4b]/98 border border-brand/30 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_40px_rgba(33,158,188,0.2)] backdrop-blur-xl transition-all duration-200 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        style={getTooltipStyle()}
      >
        <button 
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-surface-primary border border-default rounded-lg text-content-secondary cursor-pointer transition-all duration-200 hover:bg-surface-elevated hover:text-content"
          onClick={handleSkip}
        >
          <X size={18} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand/20 flex items-center justify-center text-primary">
            {step.icon}
          </div>
          <h3 className="text-xl font-bold text-content mb-2">{step.title}</h3>
          <p className="text-sm text-content-secondary leading-relaxed">{step.description}</p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {STEPS.map((_, idx) => (
            <div 
              key={idx} 
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === currentStep 
                  ? 'bg-brand w-6' 
                  : idx < currentStep 
                    ? 'bg-brand/50' 
                    : 'bg-surface-elevated'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button 
            className="py-2 px-4 bg-transparent border-none text-content-tertiary text-sm font-medium cursor-pointer transition-colors duration-200 hover:text-content"
            onClick={handleSkip}
          >
            Skip
          </button>
          
          <div className="flex gap-2">
            {!isFirstStep && (
              <button 
                className="flex items-center gap-1 py-2 px-4 bg-surface-primary border border-default rounded-lg text-content text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-surface-elevated"
                onClick={handlePrev}
              >
                <ChevronLeft size={18} />
                Back
              </button>
            )}
            <button 
              className="flex items-center gap-1 py-2 px-6 bg-gradient-to-r from-brand to-brand-hover border-none rounded-lg text-content-inverse text-sm font-semibold cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow"
              onClick={handleNext}
            >
              {isLastStep ? "Let's Go!" : 'Next'}
              {!isLastStep && <ChevronRight size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const useWalkthrough = (analysesCount: number = 0, hasResults: boolean = false) => {
  const [showNewUserWalkthrough, setShowNewUserWalkthrough] = useState(false);
  const [showAnalysisWalkthrough, setShowAnalysisWalkthrough] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('quazi_walkthrough_new_user');
    if (!completed && analysesCount === 0) {
      const timer = setTimeout(() => setShowNewUserWalkthrough(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [analysesCount]);

  useEffect(() => {
    const completed = localStorage.getItem('quazi_walkthrough_analysis');
    if (!completed && hasResults) {
      const timer = setTimeout(() => setShowAnalysisWalkthrough(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasResults]);

  const closeNewUserWalkthrough = () => setShowNewUserWalkthrough(false);
  const closeAnalysisWalkthrough = () => setShowAnalysisWalkthrough(false);
  
  const resetWalkthroughs = () => {
    localStorage.removeItem('quazi_walkthrough_new_user');
    localStorage.removeItem('quazi_walkthrough_analysis');
  };

  return { 
    showNewUserWalkthrough, 
    showAnalysisWalkthrough,
    closeNewUserWalkthrough,
    closeAnalysisWalkthrough,
    resetWalkthroughs
  };
};

export default Walkthrough;
